import React, { useState, useRef,useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { CheckBadgeIcon, ShareIcon } from "@heroicons/react/24/outline";
import { toast } from "react-toastify";
import html2canvas from "html2canvas";
import { jsPDF } from "jspdf";
import CertificateGenerator from "../../components/certificates/CertificateGenerator";
import { getMyCertificates } from "../../api/certificateApi.js";
// import badge from "../../assets/badges.png";

const Certificates = () => {
  const certRef = useRef(null);
  const [showModal, setShowModal] = useState(false);
  const [activeCert, setActiveCert] = useState(null);
  const [names, setNames] = useState({ first: "", second: "" });
  const [lockedData, setLockedData] = useState(null);
  const [userCerts, setUserCerts] = useState([]);

  useEffect(() => {
  getMyCertificates()
    .then((res) => {

      // 1. Check if res is an array. If it's already an array, use it directly.
      const rawData = Array.isArray(res) ? res : [];

const formatted = rawData.map((cert) => ({
  id: cert.id,
  certificateId: cert.certificate_serial,
  type: cert.certificate_type
    ? cert.certificate_type.toUpperCase()
    : "PARTICIPATION",

  competition: cert.competition_title,
  internship: cert.internship_title,

  duration: cert.internship_duration, // ✅ fixed

  title:
    cert.certificate_type === "internship"
      ? "Internship Certificate"
      : `Certificate of ${cert.certificate_type}`,
}));

      setUserCerts(formatted);
    })
    .catch((err) => {
      console.error("Failed to fetch certificates:", err);
      toast.error("Failed to load certificates.");
    });
}, []);


  const normalizeName = (name) =>
    name
      .trim()
      .replace(/\s+/g, " ")
      .toLowerCase()
      .replace(/\b\w/g, (c) => c.toUpperCase());

  const handleDownloadInitiate = (cert) => {
    setActiveCert(cert);
    setNames({ first: "", second: "" });
    setShowModal(true);
  };

  const handleShare = (cert) => {
    const url = `https://genzevaluation.digital/verify/${cert.certificateId}`;
    navigator.clipboard.writeText(url);
    toast.success("Verification link copied!");
  };

const finalizeDownload = async () => {
  const cleanFirst = normalizeName(names.first);
  const cleanSecond = normalizeName(names.second);

  if (!cleanFirst || cleanFirst !== cleanSecond) {
    return toast.error("Names do not match!");
  }

  const toastId = toast.loading("Preparing high-resolution render...");
  setShowModal(false);
  
  // 1. Set the data to trigger the render in the hidden div
  setLockedData({
    ...activeCert,
    name: cleanFirst,
    issueDate: new Date().toLocaleDateString("en-GB"),
  });

  // 2. Give it 1000ms. This is vital so the QR code and Badge actually finish loading.
  setTimeout(async () => {
    try {
      if (!certRef.current) throw new Error("Render container not found");

      const canvas = await html2canvas(certRef.current, {
  scale: 3, 
  useCORS: true,
  backgroundColor: "#ffffff", // Use pure white for the PDF background
  width: 1200, // Matches the wrapper width
  height: 850, // Matches the wrapper height
  x: 0,
  y: 0,
  scrollX: 0,
  scrollY: 0,
  onclone: (clonedDoc) => {
    // Force the cloned element to be visible and centered
    const el = clonedDoc.querySelector('.bg-gray-100');
    if (el) el.style.display = 'flex';
  }
});

      // 3. Use 'image/jpeg' instead of 'image/png' to avoid the "Signature" error
      const imgData = canvas.toDataURL("image/jpeg", 1.0); 
      
      const pdf = new jsPDF("l", "mm", "a4");
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = pdf.internal.pageSize.getHeight();

      // 4. Force JPEG here too
      pdf.addImage(imgData, "JPEG", 0, 0, pdfWidth, pdfHeight);
      pdf.save(`Certificate_${activeCert.certificateId}.pdf`);

      toast.update(toastId, {
        render: "Download Successful!",
        type: "success",
        isLoading: false,
        autoClose: 3000
      });
    } catch (err) {
      console.error("PDF Error:", err);
      toast.update(toastId, {
        render: "Capture failed. Check console.",
        type: "error",
        isLoading: false,
        autoClose: 3000
      });
    } finally {
      setLockedData(null);
    }
  }, 1000); 
};

  return (
    <div className="min-h-screen bg-[#050505] text-white p-12">
      {/* 🔒 HIDDEN RENDER AREA - Adjusted for better capture */}
      
<div 
  className="absolute top-0 left-0 opacity-0 pointer-events-none" 
  style={{ zIndex: -1, width: '1000px', height: '700px' }}
>
  {lockedData && (
    <div ref={certRef}>
      <CertificateGenerator {...lockedData} />
    </div>
  )}
</div>

      {/* CERTIFICATE LIST */}
      <div className="max-w-5xl mx-auto space-y-6">
        {userCerts.map((cert) => (
          <motion.div
            key={cert.id}
            whileHover={{ x: 10 }}
            className="bg-[#0a0a0a] p-8 rounded-[2rem] border border-white/5 flex justify-between items-center"
          >
            <div className="flex gap-6 items-center">
              <div
                className={`h-16 w-16 rounded-2xl bg-gradient-to-br ${
                 cert.type === "WINNER" || cert.type === "RUNNER_UP"
                    ? "from-amber-500 to-yellow-600"
                    : cert.type === "INTERNSHIP"
                    ? "from-emerald-600 to-green-500"
                    : "from-indigo-600 to-black"
                } flex items-center justify-center`}
              >
                <CheckBadgeIcon className="h-8 w-8 text-white" />
              </div>

              <div>
                <h3 className="text-xl font-black italic uppercase">
                  {cert.title}
                </h3>
                <p className="text-xs text-gray-500">
                  {cert.competition && (
                   <span>{cert.competition} • </span>
                   )}
                   {cert.certificateId}
                </p>
              </div>
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => handleShare(cert)}
                className="bg-black border border-white/10 p-3 rounded-xl hover:bg-white/5 transition-colors"
              >
                <ShareIcon className="h-5 w-5" />
              </button>

              <button
                onClick={() => handleDownloadInitiate(cert)}
                className="bg-white text-black px-6 py-3 rounded-xl font-black text-xs uppercase italic hover:bg-gray-200 transition-colors"
              >
                Download
              </button>
            </div>
          </motion.div>
        ))}
      </div>

      {/* NAME CONFIRMATION MODAL */}
      <AnimatePresence>
        {showModal && (
          <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4">
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-[#0a0a0a] p-10 rounded-3xl max-w-md w-full border border-white/10"
            >
              <h2 className="text-2xl font-black italic uppercase mb-6 text-center">
                Confirm Identity
              </h2>
              <p className="text-gray-400 text-sm mb-6 text-center">
                Please enter your full name exactly as it should appear on your{" "}
                <span className="text-white font-bold">
                  {activeCert?.type.replace("_", " ")}
                </span>{" "}
                certificate.
              </p>

              <input
                placeholder="Full Name"
                className="w-full p-4 mb-4 bg-black border border-white/10 rounded-xl focus:border-indigo-500 outline-none transition-colors"
                value={names.first}
                onChange={(e) => setNames({ ...names, first: e.target.value })}
              />
              <input
                placeholder="Confirm Full Name"
                className="w-full p-4 mb-6 bg-black border border-white/10 rounded-xl focus:border-indigo-500 outline-none transition-colors"
                value={names.second}
                onChange={(e) =>
                  setNames({ ...names, second: e.target.value })
                }
              />

              <div className="flex gap-4">
                <button
                  onClick={() => setShowModal(false)}
                  className="flex-1 bg-white/5 py-4 rounded-xl font-bold uppercase text-xs"
                >
                  Cancel
                </button>
                <button
                  onClick={finalizeDownload}
                  className="flex-[2] bg-indigo-600 py-4 rounded-xl font-black uppercase text-xs shadow-lg shadow-indigo-500/20"
                >
                  Generate PDF
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default Certificates;
